type WaitingRequest = {
  priority: number;
  start: () => void;
  cancel: () => void;
  signal?: AbortSignal | null;
};

/** Bound request bursts while leaving authentication refresh outside the queue. */
export class ApiRequestQueue {
  private active = 0;
  private readonly waiting: WaitingRequest[] = [];

  constructor(private readonly limit = 4) {}

  async run<T>(work: () => Promise<T>, signal?: AbortSignal | null, priority = 'auto'): Promise<T> {
    if (signal?.aborted) throw signal.reason ?? new DOMException('Aborted', 'AbortError');
    await new Promise<void>((resolve, reject) => {
      const item: WaitingRequest = {
        priority: priority === 'high' ? 0 : priority === 'low' ? 2 : 1,
        signal,
        start: () => { signal?.removeEventListener('abort', item.cancel); resolve(); },
        cancel: () => {
          const index = this.waiting.indexOf(item);
          if (index >= 0) this.waiting.splice(index, 1);
          reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
        },
      };
      signal?.addEventListener('abort', item.cancel, { once: true });
      this.waiting.push(item);
      this.waiting.sort((a, b) => a.priority - b.priority);
      this.drain();
    });
    try {
      if (signal?.aborted) throw signal.reason ?? new DOMException('Aborted', 'AbortError');
      return await work();
    } finally { this.active -= 1; this.drain(); }
  }

  private drain() {
    while (this.active < this.limit && this.waiting.length) {
      const item = this.waiting.shift()!;
      this.active += 1;
      item.start();
    }
  }
}
